import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Layout from '@/components/feature/Layout';
import { batchImportCompanies } from '@/api/companies';
import {
  parseRowsByHeader,
  readExcelRows,
  readCSVRows,
  rowsToPayloads,
  type ParsedRow,
} from '@/lib/companyImport';

interface ImportHistoryItem {
  id: string;
  fileName: string;
  totalRows: number;
  successRows: number;
  failRows: number;
  importedAt: string;
}

type PreviewRow = ParsedRow;

const CSV_TEMPLATE = '企业名称,统一信用代码,地址,网址,类型,负责人,业务标签(多个用;分隔),来源标签(多个用;分隔),联系人姓名,联系人电话,联系人职位\n示例公司,91110000XXXXXXXX,北京市朝阳区XX路XX号,www.example.com,实体企业,李经理,绿证;碳交易,绿色工厂,张三,13800000001,总经理';

function isExcelFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase();
  return ext === 'xlsx' || ext === 'xls';
}

function isCSVFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase();
  return ext === 'csv';
}

function isSupportedFile(name: string): boolean {
  return isCSVFile(name) || isExcelFile(name);
}

// 导入历史仅记录本会话内真实发生的导入(未持久化到后端,刷新即清空)
const initialImportHistory: ImportHistoryItem[] = [];

const FIELD_LABELS: Record<string, string> = {
  name: '企业名称', creditCode: '统一信用代码', address: '地址', website: '网址',
  type: '类型', responsiblePerson: '负责人', businessTags: '业务标签', sourceTags: '来源标签',
  contactName: '联系人', contactPhone: '联系电话', contactPosition: '职位',
};

export default function ImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importResult, setImportResult] = useState<{ total: number; success: number; fail: number } | null>(null);
  const [history, setHistory] = useState<ImportHistoryItem[]>(initialImportHistory);
  const [matchedFields, setMatchedFields] = useState<string[]>([]);

  // rows 含表头行,按表头名称智能匹配列(顺序任意皆可对上)。
  const processRows = useCallback((rows: string[][], fileLabel: string) => {
    const result = parseRowsByHeader(rows);
    if (result.fatalError) {
      setError(result.fatalError);
      return;
    }
    if (result.rows.length === 0) {
      setError('未能解析到有效数据行，请检查文件内容');
      return;
    }
    setFileName(fileLabel);
    setPreview(result.rows);
    setMatchedFields(result.matchedFields);
  }, []);

  const parseFile = useCallback((file: File) => {
    setError('');
    const name = file.name;

    if (!isSupportedFile(name)) {
      setError('仅支持 .csv、.xlsx、.xls 格式文件');
      return;
    }

    if (isCSVFile(name)) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const text = evt.target?.result as string;
          const rows = readCSVRows(text);
          processRows(rows, name);
        } catch {
          setError('CSV 文件解析失败，请检查文件编码是否为 UTF-8');
        }
      };
      reader.onerror = () => setError('文件读取失败');
      reader.readAsText(file, 'UTF-8');
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const buffer = evt.target?.result as ArrayBuffer;
          const rows = readExcelRows(buffer);
          processRows(rows, name);
        } catch {
          setError('Excel 文件解析失败，请检查文件格式');
        }
      };
      reader.onerror = () => setError('文件读取失败');
      reader.readAsArrayBuffer(file);
    }
  }, [processRows]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && isSupportedFile(file.name)) {
      parseFile(file);
    } else {
      setError('仅支持 .csv、.xlsx、.xls 格式文件');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleImport = async () => {
    const validRows = preview.filter((r) => r.valid);
    if (validRows.length === 0) {
      setError('没有有效数据可导入');
      return;
    }

    setError('');
    setImporting(true);

    const companies = rowsToPayloads(validRows);

    try {
      const created = await batchImportCompanies({ companies });
      const successCount = created.length;
      setImportDone(true);
      setImportResult({
        total: preview.length,
        success: successCount,
        fail: preview.length - successCount,
      });

      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setHistory((prev) => [{
        id: `h${Date.now()}`,
        fileName: fileName,
        totalRows: preview.length,
        successRows: successCount,
        failRows: preview.length - successCount,
        importedAt: dateStr,
      }, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : '导入失败，请稍后重试');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setPreview([]);
    setFileName('');
    setError('');
    setImportDone(false);
    setImportResult(null);
    setMatchedFields([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadCSVTemplate = () => {
    const blob = new Blob(['\uFEFF' + CSV_TEMPLATE], { type: 'text/csv;charset=UTF-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '企业导入模板.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcelTemplate = () => {
    const headerRow = ['企业名称', '统一信用代码', '地址', '网址', '类型', '负责人', '业务标签(多个用;分隔)', '来源标签(多个用;分隔)', '联系人姓名', '联系人电话', '联系人职位'];
    const sampleRow = ['示例公司', '91110000XXXXXXXX', '北京市朝阳区XX路XX号', 'www.example.com', '实体企业', '李经理', '绿证;碳交易', '绿色工厂', '张三', '13800000001', '总经理'];

    const ws = XLSX.utils.aoa_to_sheet([headerRow, sampleRow]);
    ws['!cols'] = headerRow.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '企业数据');

    XLSX.writeFile(wb, '企业导入模板.xlsx');
  };

  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  const validCount = preview.filter((r) => r.valid).length;
  const failCount = preview.filter((r) => !r.valid).length;

  const getFileIcon = (fname: string) => {
    if (fname.endsWith('.xlsx') || fname.endsWith('.xls')) return 'ri-file-excel-2-line text-accent-500';
    return 'ri-file-excel-2-line text-accent-500';
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">数据导入</h1>
            <p className="text-sm text-foreground-500 mt-1">通过 CSV 或 Excel 文件批量导入企业数据，支持预览和校验</p>
          </div>
          {preview.length > 0 && !importDone && (
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2.5 border border-background-200 text-foreground-600 hover:text-foreground-800 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-base"></i>
              重新选择
            </button>
          )}
        </div>

        {/* Upload area */}
        {preview.length === 0 && !importDone && (
          <>
            <div
              ref={dropZoneRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
                dragOver
                  ? 'border-primary-400 bg-primary-50/50'
                  : 'border-background-300 bg-background-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-accent-50 flex items-center justify-center">
                  <i className="ri-file-excel-2-line text-3xl text-accent-500"></i>
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground-800">
                    拖拽文件到此处，或点击下方按钮选择
                  </h3>
                  <p className="text-sm text-foreground-500 mt-1">
                    支持 .csv、.xlsx、.xls 格式，UTF-8 编码，单文件上传
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-folder-open-line mr-1.5"></i>
                    选择文件
                  </button>
                  {/* Template download dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                      className="flex items-center gap-1.5 px-4 py-2.5 border border-background-200 hover:border-accent-300 rounded-lg text-sm font-medium text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line text-sm"></i>
                      下载模板
                      <i className="ri-arrow-down-s-line text-sm ml-0.5"></i>
                    </button>
                    {showTemplateMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowTemplateMenu(false)}
                        />
                        <div className="absolute top-full mt-1 left-0 z-20 bg-background-50 rounded-lg border border-background-200 shadow-sm py-1 min-w-[160px]">
                          <button
                            onClick={() => { downloadCSVTemplate(); setShowTemplateMenu(false); }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-file-text-line text-accent-500"></i>
                            CSV 模板 (.csv)
                          </button>
                          <button
                            onClick={() => { downloadExcelTemplate(); setShowTemplateMenu(false); }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-file-excel-2-line text-green-500"></i>
                            Excel 模板 (.xlsx)
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Format guide */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200/70">
                <h3 className="text-sm font-heading font-semibold text-foreground-800">
                  <i className="ri-information-line mr-1.5 text-foreground-500"></i>
                  文件格式规范（CSV / Excel 通用）
                </h3>
              </div>
              <div className="p-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-2.5 px-3 font-medium text-foreground-600 whitespace-nowrap">列序号</th>
                      <th className="text-left py-2.5 px-3 font-medium text-foreground-600 whitespace-nowrap">字段名</th>
                      <th className="text-left py-2.5 px-3 font-medium text-foreground-600 whitespace-nowrap">说明</th>
                      <th className="text-left py-2.5 px-3 font-medium text-foreground-600 whitespace-nowrap">必填</th>
                      <th className="text-left py-2.5 px-3 font-medium text-foreground-600 whitespace-nowrap">示例</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/70">
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">A</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">企业名称</td>
                      <td className="py-2.5 px-3 text-foreground-600">公司全称</td>
                      <td className="py-2.5 px-3"><span className="text-red-500 text-xs font-medium">是</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">华能国际电力股份有限公司</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">B</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">统一信用代码</td>
                      <td className="py-2.5 px-3 text-foreground-600">18位统一社会信用代码</td>
                      <td className="py-2.5 px-3"><span className="text-red-500 text-xs font-medium">是</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">91110000100001508D</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">C</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">地址</td>
                      <td className="py-2.5 px-3 text-foreground-600">公司注册/办公地址</td>
                      <td className="py-2.5 px-3"><span className="text-red-500 text-xs font-medium">是</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">北京市西城区复兴门内大街6号</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">D</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">网址</td>
                      <td className="py-2.5 px-3 text-foreground-600">公司官网</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">www.example.com</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">E</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">类型</td>
                      <td className="py-2.5 px-3 text-foreground-600">实体企业 / 认证机构 / 第三方咨询机构</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">实体企业</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">F</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">负责人</td>
                      <td className="py-2.5 px-3 text-foreground-600">企业负责人姓名</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">李经理</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">G</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">业务标签</td>
                      <td className="py-2.5 px-3 text-foreground-600">多个用 ; 分隔</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">绿证;碳交易</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">H</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">来源标签</td>
                      <td className="py-2.5 px-3 text-foreground-600">多个用 ; 分隔</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">绿色工厂;碳中和先锋</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">I</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">联系人姓名</td>
                      <td className="py-2.5 px-3 text-foreground-600">主要联系人</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">张三</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">J</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">联系人电话</td>
                      <td className="py-2.5 px-3 text-foreground-600">联系电话</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">13800000001</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 text-foreground-500">K</td>
                      <td className="py-2.5 px-3 text-foreground-800 font-medium">联系人职位</td>
                      <td className="py-2.5 px-3 text-foreground-600">职位名称</td>
                      <td className="py-2.5 px-3"><span className="text-foreground-400 text-xs">否</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs">总经理</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Success state */}
        {importDone && importResult && (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-accent-500"></i>
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-900">导入完成</h3>
                <p className="text-sm text-foreground-500 mt-1">文件 &quot;{fileName}&quot; 处理完毕</p>
              </div>
              <div className="flex items-center gap-6 mt-2">
                <div className="text-center">
                  <p className="text-2xl font-heading font-bold text-foreground-900">{importResult.total}</p>
                  <p className="text-xs text-foreground-500">总行数</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-heading font-bold text-accent-600">{importResult.success}</p>
                  <p className="text-xs text-foreground-500">成功</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-heading font-bold text-red-500">{importResult.fail}</p>
                  <p className="text-xs text-foreground-500">失败</p>
                </div>
              </div>
              <button
                onClick={handleReset}
                className="mt-4 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1.5"></i>
                导入新文件
              </button>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview.length > 0 && !importDone && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <i className="ri-file-list-3-line text-foreground-500"></i>
                <span className="text-sm font-medium text-foreground-800">{fileName}</span>
                <span className="text-xs text-foreground-500">
                  共 {preview.length} 行，{validCount} 条有效，{failCount} 条异常
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {failCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 rounded-md text-xs font-medium">
                    <i className="ri-error-warning-line text-sm"></i>
                    {failCount} 条异常
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-50 text-accent-600 rounded-md text-xs font-medium">
                  <i className="ri-check-line text-sm"></i>
                  {validCount} 条有效
                </span>
              </div>
            </div>
            {matchedFields.length > 0 && (
              <div className="px-5 py-3 border-b border-background-200/70 bg-background-100/40 flex items-start gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 flex items-center gap-1 mt-0.5">
                  <i className="ri-links-line"></i>已按表头识别到以下列:
                </span>
                {matchedFields.map((f) => (
                  <span key={f} className="inline-flex px-2 py-0.5 bg-primary-50 text-primary-600 rounded text-[11px] font-medium">
                    {FIELD_LABELS[f] || f}
                  </span>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-background-100/70 border-b border-background-200">
                    <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap w-10">#</th>
                    <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">企业名称</th>
                    <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">类型</th>
                    <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">业务标签</th>
                    <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">联系人</th>
                    <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">状态</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {preview.map((row) => (
                    <tr key={row.index} className={row.valid ? 'hover:bg-background-50' : 'bg-red-50/50'}>
                      <td className="px-3 py-2.5 text-foreground-500">{row.index}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-foreground-800 font-medium max-w-[200px] truncate block">{row.name}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex px-2 py-0.5 bg-background-100 rounded text-foreground-600">
                          {row.type || '实体企业'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-foreground-600 max-w-[150px] truncate">{row.businessTags || '-'}</td>
                      <td className="px-3 py-2.5 text-foreground-600">{row.contactName || '-'}</td>
                      <td className="px-3 py-2.5">
                        {row.valid ? (
                          <span className="inline-flex items-center gap-1 text-accent-600 text-xs font-medium">
                            <i className="ri-check-line"></i>有效
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium cursor-help" title={row.error}>
                            <i className="ri-close-line"></i>{row.error}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && preview.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Action bar */}
        {preview.length > 0 && !importDone && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              将导入 <strong className="text-foreground-700">{validCount}</strong> 条有效数据
              {failCount > 0 && (
                <span className="text-red-500 ml-2">（{failCount} 条异常数据将被跳过）</span>
              )}
            </p>
            <button
              onClick={handleImport}
              disabled={importing || validCount === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              {importing ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  正在导入...
                </>
              ) : (
                <>
                  <i className="ri-upload-line text-base"></i>
                  确认导入 {validCount} 条数据
                </>
              )}
            </button>
          </div>
        )}

        {/* Import history */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-200/70">
            <h3 className="text-sm font-heading font-semibold text-foreground-800">
              <i className="ri-history-line mr-1.5 text-foreground-500"></i>
              导入历史
            </h3>
          </div>
          {history.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <div className="flex flex-col items-center gap-2">
                <i className="ri-history-line text-2xl text-foreground-300"></i>
                <p className="text-sm text-foreground-500">暂无导入记录</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100/70 border-b border-background-200">
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-foreground-500 uppercase whitespace-nowrap">文件名</th>
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-foreground-500 uppercase whitespace-nowrap">总行数</th>
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-foreground-500 uppercase whitespace-nowrap">成功</th>
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-foreground-500 uppercase whitespace-nowrap">失败</th>
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-foreground-500 uppercase whitespace-nowrap">导入时间</th>
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-foreground-500 uppercase whitespace-nowrap">状态</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {history.map((item) => (
                    <tr key={item.id} className="hover:bg-background-50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <i className={getFileIcon(item.fileName)}></i>
                          <span className="text-foreground-800 font-medium">{item.fileName}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-foreground-600">{item.totalRows}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1 text-accent-600 text-xs font-medium">
                          <i className="ri-check-line"></i>
                          {item.successRows}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {item.failRows > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium">
                            <i className="ri-close-line"></i>
                            {item.failRows}
                          </span>
                        ) : (
                          <span className="text-foreground-400">0</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-foreground-500 text-xs">{item.importedAt}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          item.failRows === 0
                            ? 'bg-accent-50 text-accent-700'
                            : 'bg-secondary-50 text-secondary-700'
                        }`}>
                          {item.failRows === 0 ? '全部成功' : '部分成功'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}